import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { FileText, X, CheckCircle, AlertCircle, RefreshCw, ArrowLeft, Eye, Download, Send, Share2, Hash, Target, Plus, Zap, Gauge, Thermometer, Activity, Wifi, Settings, User } from 'lucide-react';
import { getJwtToken } from '../../../../../services/auth';
import { extractM500ZoneDetailsFromData } from '../../../../../utils/m500ZoneUtils';
import { formatDateTime} from '../../../../../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings } from '../../../../../utils/unitUtils';
import { formatHeatInputLabel } from '../../../../../utils/weldParameterLabels';
import { logPqrReportGenerated } from '../../../utils/productionAudit';
import { useProductionModalAudit } from '../../../../audit/useProductionModalAudit';

type PqrPassViewReport = {
  id: number;
  weld_number: string;
  generated_at?: string;
  report_url: string;
  project_id?: string | number;
};

type ExcludedTlogsItem = {
  torch: string;
  pass_name: string;
  weld_uuid: string;
  weld_number: string;
  excluded_tlogs: string[];
};

type PqrReportsWithPassViewItem = {
  id: number;
  project_id: number;
  weld_numbers: string; // "9,8"
  pqr_number?: string;
  boundary: number; // 10
  /** 'avg' | 'minmax' (null/undefined => treat as 'avg') */
  pqr_limits_mode?: string | null;
  last_generated_at?: string;
  pqr_report_url?: string;
  pass_view_reports?: PqrPassViewReport[];
  excluded_tlogs_key?: { excluded_tlogs: ExcludedTlogsItem[] };
};

type PqrReportsWithPassViewResponse = {
  success: number;
  message?: string;
  statusCode?: number;
  data?: {
    pqrReports?: PqrReportsWithPassViewItem[];
  };
};

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  /* For Firefox */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #f1f1f1;
  }

  /* Smooth scrolling */
  .scrollbar-thin {
    scroll-behavior: smooth;
  }
`;

interface PqrReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedWeldNumbers: string[];
  selectedWeldAndJobNumbers?: string[];
  projectId?: number;
  projectName?: string;
  project?: any;
  pipeSize?: string;
}

export const PqrReportModal: React.FC<PqrReportModalProps> = ({
  isOpen,
  onClose,
  selectedWeldNumbers,
  selectedWeldAndJobNumbers = [],
  projectId,
  projectName,
  project,
  pipeSize: selectedPipeSize,
}) => {
  useProductionModalAudit(isOpen, 'PQR Report', projectId, projectName);
  const [selectedWeldNumbersForApi, setSelectedWeldNumbersForApi] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showExistingReports, setShowExistingReports] = useState(true);
  const [showCreatePqrMode, setShowCreatePqrMode] = useState(false);
  const [showCreatePqrModal, setShowCreatePqrModal] = useState(false);
  const [editedSummaryValues, setEditedSummaryValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<any>(null);
  const [selectedPassName, setSelectedPassName] = useState<string | null>(null);
  const [showDetailsTable, setShowDetailsTable] = useState(false);
  const [showLogTable, setShowLogTable] = useState(false);
  const [logData, setLogData] = useState<any>(null);
  const [isLoadingLog, setIsLoadingLog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  // Log table checkbox state: store selected tlog ids (NOT row indices)
  // In this UI, "checked" means "exclude this tlog id" (it is included in excludedTlogsData for the weld context).
  const [selectedLogRecords, setSelectedLogRecords] = useState<Set<string>>(new Set());
  const [showPassViewReportsModal, setShowPassViewReportsModal] = useState(false);
  const [selectedPassViewReports, setSelectedPassViewReports] = useState<PqrPassViewReport[]>([]);
  const [tolerance, setTolerance] = useState<string>('10%');
  // Limits selection for PQR summary limits (drives how min/max are chosen for report generation)
  const [pqrLimitsMode, setPqrLimitsMode] = useState<'minmax' | 'avg'>('avg');
  const [toleranceUsedInLastGenerate, setToleranceUsedInLastGenerate] = useState<string | null>(null);
  const [createPqrReportName, setCreatePqrReportName] = useState<string>('');
  const [existingReportInfo, setExistingReportInfo] = useState<any>(null);
  const [currentPqrReportUrl, setCurrentPqrReportUrl] = useState<string | null>(null);
  const [currentPqrNumber, setCurrentPqrNumber] = useState<string | null>(null);
  const [isLoadingExistingReport, setIsLoadingExistingReport] = useState(false);
  const [isExcludingTlogs, setIsExcludingTlogs] = useState(false);
  const [excludedTlogsData, setExcludedTlogsData] = useState<any[]>([]);
  const [reportsWithPassView, setReportsWithPassView] = useState<PqrReportsWithPassViewItem[]>([]);
  const [isLoadingReportsWithPassView, setIsLoadingReportsWithPassView] = useState(false);
  const [reportsWithPassViewError, setReportsWithPassViewError] = useState<string | null>(null);

  const effectiveSelectedWeldNumbers = useMemo(() => {
    return selectedWeldNumbersForApi;
  }, [selectedWeldNumbersForApi]);

  const normalizePqrLimitsMode = (mode: unknown): 'minmax' | 'avg' => {
    if (mode == null) return 'avg';
    const s = String(mode).trim().toLowerCase();
    if (!s) return 'avg';
    if (s === 'minmax') return 'minmax';
    if (s === 'avg') return 'avg';
    // Be permissive with legacy / descriptive values
    if (s.includes('min') && s.includes('max')) return 'minmax';
    if (s.includes('avg')) return 'avg';
    return 'avg';
  };

  const hasUpdateChanges = useMemo(() => {
    const toleranceChanged = toleranceUsedInLastGenerate != null && tolerance !== toleranceUsedInLastGenerate;
    const hasEdits = Object.keys(editedSummaryValues).length > 0;
    const hasLogSelection = selectedLogRecords.size > 0;
    return toleranceChanged || hasEdits || hasLogSelection;
  }, [tolerance, toleranceUsedInLastGenerate, editedSummaryValues, selectedLogRecords.size]);

  // When the modal opens/closes, reset selection + results
  useEffect(() => {
    if (!isOpen) {
      setSelectedWeldNumbersForApi([]);
      setReportsWithPassView([]);
      setReportsWithPassViewError(null);
      setIsLoadingReportsWithPassView(false);
      setShowExistingReports(true);
      setShowCreatePqrMode(false);
      setEditedSummaryValues({});
      setToleranceUsedInLastGenerate(null);
      setCurrentPqrReportUrl(null);
      setCurrentPqrNumber(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleBackToExistingReports = () => {
    setShowExistingReports(true);
    // Hide generated views when going back to the existing reports list
    setIsSuccess(false);
    setError(null);
    setResponseData(null);
    setToleranceUsedInLastGenerate(null);
    setCurrentPqrReportUrl(null);
    setCurrentPqrNumber(null);
    setEditedSummaryValues({});
    setSelectedPassName(null);
    setShowDetailsTable(false);
    setShowLogTable(false);
    setLogData(null);
    setIsLoadingLog(false);
    setSelectedRecord(null);
    setSelectedLogRecords(new Set());
    setExcludedTlogsData([]);
    setIsExcludingTlogs(false);
    setExistingReportInfo(null);
  };

  const fetchReportsWithPassView = async (signal?: AbortSignal) => {
    if (!projectId) return;

    setIsLoadingReportsWithPassView(true);
    setReportsWithPassViewError(null);

    try {
      const token = await getJwtToken();
      const baseUrl = process.env.REACT_APP_LIMITS_API_BASE_URL;
      const endpoint = `${baseUrl}/pqr/reports-with-pass-view?project_id=${projectId}`;
      // const endpoint = `https://by2e8b0nwd.execute-api.us-west-2.amazonaws.com/dev/v1/pqr/reports-with-pass-view?project_id=${projectId}`;

      const response = await fetch(endpoint, {
        method: 'GET',
        signal,
        headers: {
          'Accept': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as PqrReportsWithPassViewResponse;
      const rows = data?.data?.pqrReports ?? [];
      setReportsWithPassView(Array.isArray(rows) ? rows : []);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching reports-with-pass-view:', err);
      setReportsWithPassViewError(err?.message || 'Failed to fetch PQR reports');
      setReportsWithPassView([]);
    } finally {
      setIsLoadingReportsWithPassView(false);
    }
  };

  // Fetch existing PQR reports list when modal opens (does NOT generate reports)
  useEffect(() => {
    if (!isOpen || !projectId) return;
    const controller = new AbortController();
    fetchReportsWithPassView(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  const mapWeldNumberToOption = (weld: string) => {
    const w = weld.trim();
    if (selectedWeldNumbers.includes(w)) return w;
    const withW = `W${w}`;
    if (selectedWeldNumbers.includes(withW)) return withW;
    return w;
  };
  
  // Use dummy email
  // const emailAddress = 'sarin.m@crce.com';

  // Fetch existing PQR reports info
  const fetchExistingReportsInfo = async () => {
    if (!projectId || effectiveSelectedWeldNumbers.length === 0) {
      return;
    }

    // Avoid overlapping polling calls
    if (isLoadingExistingReport) return;

    setIsLoadingExistingReport(true);
    try {
      // Parse tolerance to get boundary number (remove % and convert to number)
      const boundary = parseInt(tolerance.replace('%', '')) || 10;
      
      // Format weld numbers: extract just the numbers (e.g., "W001" -> "001" or just use the numbers)
      // Assuming selectedWeldNumbers contains weld numbers like "W001", "W002", etc.
      const weldNumbersString = effectiveSelectedWeldNumbers
        .map(weld => weld.replace(/^W/i, '')) // Remove 'W' prefix if present
        .join(',');

      const baseUrl = process.env.REACT_APP_LIMITS_API_BASE_URL;

      const endpoint = `${baseUrl}/pqr/reports-info?project_id=${projectId}&weld_numbers=${encodeURIComponent(weldNumbersString)}&boundary=${boundary}`;

      const token = await getJwtToken();
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setExistingReportInfo(data);
      console.log('Existing PQR reports info:', data);
    } catch (err: any) {
      console.error('Error fetching existing reports info:', err);
      // Don't set error state here, just log it
    } finally {
      setIsLoadingExistingReport(false);
    }
  };


  // Map internal field keys to API field names
  const mapFieldKeyToApiName = (fieldKey: string): string => {
    const fieldMapping: Record<string, string> = {
      preHeatMin: 'preHeatInterPassTemperatureMin',
      preHeatMax: 'preHeatInterPassTemperatureMax',
      arcEnergyMin: 'arcEnergyMin',
      arcEnergyMax: 'arcEnergyMax',
      pulseFreqMin: 'averagePulseFrequencyMin',
      pulseFreqMax: 'averagePulseFrequencyMax',
      pulseTimeMin: 'averagePulseTimeMin',
      pulseTimeMax: 'averagePulseTimeMax',
      peakCurrentMin: 'averagePeakCurrentMin',
      peakCurrentMax: 'averagePeakCurrentMax',
      baseCurrentMin: 'averageBaseCurrentMin',
      baseCurrentMax: 'averageBaseCurrentMax',
    };
    return fieldMapping[fieldKey] || fieldKey;
  };

  // Collect edited values and format them as items array for API
  const collectEditedItems = (): any[] => {
    if (!editedSummaryValues || Object.keys(editedSummaryValues).length === 0) {
      return [];
    }

    // Get all editable columns to know which fields to include
    const editableColumns = columnDefinitions.filter(col => col.editable);

    // Find all unique pass names that have been edited
    const editedPassNames = new Set<string>();
    Object.keys(editedSummaryValues).forEach(key => {
      const [passName] = key.split(':');
      if (passName) {
        editedPassNames.add(passName);
      }
    });

    // Build items array for each edited pass
    const items = Array.from(editedPassNames).map(passName => {
      // Find the original row data for this pass
      const originalRow = createDataFromPqrReport.find(row => row.passName === passName);
      
      // Initialize item with passName
      const item: any = { passName: passName || '-' };

      // For each editable column, use edited value if available, otherwise use original value
      editableColumns.forEach(column => {
        const minApiName = mapFieldKeyToApiName(column.minKey);
        const maxApiName = mapFieldKeyToApiName(column.maxKey);
        
        // Check if min value was edited
        const editedMinKey = `${passName}:${column.minKey}`;
        const editedMinValue = editedSummaryValues[editedMinKey];
        
        if (editedMinValue !== undefined) {
          // Use edited value
          const numValue = parseFloat(editedMinValue);
          item[minApiName] = isNaN(numValue) ? '0.00' : numValue.toFixed(2);
        } else {
          // Use original value from table data
          const originalValue = (originalRow as any)?.[column.minKey] ?? '0';
          const numValue = parseFloat(String(originalValue));
          item[minApiName] = isNaN(numValue) ? '0.00' : numValue.toFixed(2);
        }

        // Check if max value was edited
        const editedMaxKey = `${passName}:${column.maxKey}`;
        const editedMaxValue = editedSummaryValues[editedMaxKey];
        
        if (editedMaxValue !== undefined) {
          // Use edited value
          const numValue = parseFloat(editedMaxValue);
          item[maxApiName] = isNaN(numValue) ? '0.00' : numValue.toFixed(2);
        } else {
          // Use original value from table data
          const originalValue = (originalRow as any)?.[column.maxKey] ?? '0';
          const numValue = parseFloat(String(originalValue));
          item[maxApiName] = isNaN(numValue) ? '0.00' : numValue.toFixed(2);
        }
      });

      return item;
    });

    return items;
  };

  // Helper to get a log record's id (for matching with excluded_tlogs)
  const getLogRecordId = (record: any): string => {
    // Prefer tlog_id so it matches excluded_tlogs ids from API
    if (record?.tlog_id != null) return String(record.tlog_id);
    if (record?.event_id != null) return String(record.event_id);
    if (record?.id != null) return String(record.id);
    if (record?.time != null) return String(record.time);
    return '';
  };

  const runGeneratePqrReport = async (args: {
      weldNumbers: string[];
      boundary: number;
      tolerance?: string;
      pqrNumber?: string;
      /** Excluded tlogs from reports-with-pass-view API; used when opening existing PQR (not from log table) */
      excludedTlogsFromView?: ExcludedTlogsItem[] | null;
      /** Override pqr limits mode for this generate call (ex: when opening existing PQR row) */
      limitsMode?: 'minmax' | 'avg';
    }) => {
    if (!projectId || args.weldNumbers.length === 0) {
      setError('Project ID and weld numbers are required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsSuccess(false);

    try {
      // Excluded tlogs for generate-pqr API:
      // - From log table + Update: use current checkbox state (checked ids = excluded).
      // - Apart from log table (e.g. opening existing PQR via eye): use excludedTlogsFromView from reports-with-pass-view, or excludedTlogsData.
      let excludedTlogsForGenerate: ExcludedTlogsItem[] = excludedTlogsData;
      if (logData?.data?.data && selectedRecord) {
        // Update from log table: ensure excluded list for this weld matches current checkbox state
        const logRecords = logData.data.data;
        const rawWeld = selectedRecord.weldNumber ?? selectedRecord.weld_number ?? '';
        const weldNum = typeof rawWeld === 'string' && rawWeld.includes('|') ? rawWeld.split('|')[0].trim() : rawWeld;
        const passName = selectedRecord.passName ?? selectedRecord.pass_name ?? '';
        const torch = selectedRecord.torch ?? '';
        const weldUuid = selectedRecord.welduid_s_event_torch ?? '';
        const excludedIds: string[] = [];
        logRecords.forEach((record: any) => {
          const id = getLogRecordId(record);
          if (id && selectedLogRecords.has(id)) excludedIds.push(id);
        });
        const currentItem: ExcludedTlogsItem = {
          pass_name: passName,
          torch,
          weld_number: weldNum,
          weld_uuid: weldUuid,
          excluded_tlogs: excludedIds,
        };
        const otherItems = excludedTlogsData.filter((item: any) => {
          const itemWeld = (item.weld_number ?? '').toString();
          return !((item.pass_name ?? '') === passName && (item.torch ?? '') === torch && (itemWeld === weldNum || itemWeld === rawWeld));
        });
        excludedTlogsForGenerate = otherItems.concat(currentItem);
        setExcludedTlogsData(excludedTlogsForGenerate);
      } else if (args.excludedTlogsFromView != null && args.excludedTlogsFromView.length > 0) {
        // Opening existing PQR (eye): use excluded tlogs from reports-with-pass-view API
        excludedTlogsForGenerate = args.excludedTlogsFromView;
        setExcludedTlogsData(args.excludedTlogsFromView);
      }

      const weldNumbersString = args.weldNumbers.join(',');
      const boundary = args.boundary;

      // Get JWT token for authorization
      const token = await getJwtToken();

      // Collect edited values from the summary table
      const editedItems = collectEditedItems();

      const requestBody: any = {
        projectId: projectId,
        weldNumbers: weldNumbersString,
        boundary,
        columns_add: [],
        excluded_tlogs: excludedTlogsForGenerate,
        // Backend may use this to decide MinOfMin/MaxOfMax vs MinOfAvg/MaxOfAvg while computing limits.
        // Kept as a simple string for compatibility; if backend ignores unknown fields, this is a no-op.
        pqr_limits_mode: args.limitsMode ?? pqrLimitsMode, // 'minmax' | 'avg'
      };

      if (args.pqrNumber != null && args.pqrNumber.trim() !== '') {
        requestBody.pqr_number = args.pqrNumber.trim();
      }

      // Add items array if there are edited values
      if (editedItems.length > 0) {
        requestBody.items = editedItems;
      }

      const baseUrl = process.env.REACT_APP_LIMITS_API_BASE_URL;
     // const  baseUrl = 'http://localhost:8000/v1'

      const response = await fetch(`${baseUrl}/pqr/generate-pqr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setIsSuccess(true);
      setResponseData(data);
      const reportUrl =
        data?.data?.data?.pqr_report_url ??
        data?.data?.pqr_report_url ??
        data?.pqr_report_url ??
        data?.data?.report_url;
      if (reportUrl) setCurrentPqrReportUrl(reportUrl);
      if (args.pqrNumber != null && args.pqrNumber.trim() !== '') {
        setCurrentPqrNumber(args.pqrNumber.trim());
      }
      if (args.tolerance != null) {
        setToleranceUsedInLastGenerate(args.tolerance);
      }

      logPqrReportGenerated({
        projectId: projectId ?? null,
        projectName: projectName ?? null,
        pipeSize: selectedPipeSize ?? null,
        weldNumbers: args.weldNumbers,
        pqrNumber: args.pqrNumber ?? null,
        tolerance: args.tolerance ?? null,
        pqrLimitsMode: args.limitsMode ?? pqrLimitsMode,
        reportUrl: reportUrl ?? null,
      });

      console.log('PQR Report generated successfully:', data);
    } catch (err: any) {
      console.error('Error generating PQR report:', err);
      setError(err.message || 'Failed to generate PQR report');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePqrReport = async () => {
    const boundary = parseInt(tolerance.replace('%', '')) || 10;
    setShowExistingReports(false);
    await runGeneratePqrReport({ weldNumbers: effectiveSelectedWeldNumbers, boundary, tolerance, pqrNumber: createPqrReportName });
  };

  const handleViewPqrFromRow = async (
    welds: string[],
    boundary?: number,
    excelReportUrl?: string | null,
    pqrNumber?: string | null,
    excludedTlogsFromApi?: ExcludedTlogsItem[] | null,
    pqrLimitsModeFromApi?: string | null
  ) => {
    const mapped = welds.map(mapWeldNumberToOption);
    const b = boundary ?? 10;
    const modeForThisView = normalizePqrLimitsMode(pqrLimitsModeFromApi);

    // Pass the existing report's Excel URL and PQR number so the header shows them
    setCurrentPqrReportUrl(excelReportUrl ?? null);
    setCurrentPqrNumber(pqrNumber ?? null);

    // Pre-populate excluded tlogs from reports-with-pass-view API (used for generate-pqr and log table initial state)
    const initialExcluded = excludedTlogsFromApi && excludedTlogsFromApi.length > 0 ? excludedTlogsFromApi : [];
    setExcludedTlogsData(initialExcluded);

    // Keep header controls in sync with what the user clicked
    setSelectedWeldNumbersForApi(mapped);
    setTolerance(`${b}%`);
    // Ensure rendering + payload use the row's mode (null => avg default)
    setPqrLimitsMode(modeForThisView);

    // Reset drill-down state so the generated table starts from summary view
    setSelectedPassName(null);
    setShowDetailsTable(false);
    setShowLogTable(false);
    setLogData(null);
    setIsLoadingLog(false);
    setSelectedRecord(null);
    setSelectedLogRecords(new Set());
    setIsExcludingTlogs(false);
    setExistingReportInfo(null);
    setShowExistingReports(false);

    // Pass excluded tlogs from API to generate-pqr (apart from log table; log table uses checkbox state on Update)
    await runGeneratePqrReport({
      weldNumbers: mapped,
      boundary: b,
      tolerance: `${b}%`,
      excludedTlogsFromView: initialExcluded.length > 0 ? initialExcluded : undefined,
      limitsMode: modeForThisView,
    });
  };

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      setIsSuccess(false);
      setError(null);
      setResponseData(null);
      setSelectedPassName(null);
      setShowDetailsTable(false);
      setShowLogTable(false);
      setLogData(null);
      setIsLoadingLog(false);
      setSelectedRecord(null);
      setSelectedLogRecords(new Set());
      setExistingReportInfo(null);
      setIsLoadingExistingReport(false);
      setExcludedTlogsData([]);
      setIsExcludingTlogs(false);
      setShowCreatePqrMode(false);
    }
  }, [isOpen]);

  // Inject custom scrollbar styles
  useEffect(() => {
    const styleId = 'pqr-report-scrollbar-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = scrollbarStyles;
      document.head.appendChild(style);
    }

    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  // When log data is loaded: check rows whose tlog id is present in excludedTlogsData for this weld.
  useEffect(() => {
    if (!logData?.data?.data || !Array.isArray(logData.data.data) || !selectedRecord) return;
    const logRecords = logData.data.data;
    const rawWeld = selectedRecord.weldNumber ?? selectedRecord.weld_number ?? '';
    const weldNum = typeof rawWeld === 'string' && rawWeld.includes('|') ? rawWeld.split('|')[0].trim() : rawWeld;

    const normalizeWeld = (w: unknown) => String(w ?? '').trim().replace(/^W/i, '');
    const rawWeldNorm = normalizeWeld(rawWeld);
    const weldNumNorm = normalizeWeld(weldNum);

    // IDs coming from API for this weld that should be checked (excluded) in the log table
    const apiSelectedIds = new Set<string>();
    excludedTlogsData.forEach((item: any) => {
      const itemWeldNorm = normalizeWeld(item?.weld_number);
      if (itemWeldNorm !== rawWeldNorm && itemWeldNorm !== weldNumNorm) return;
      (item.excluded_tlogs ?? []).forEach((id: string) => {
        if (id != null && String(id).trim() !== '') apiSelectedIds.add(String(id));
      });
    });

    // Only keep ids that exist in the current log dataset
    const idsToSelect = new Set<string>();
    logRecords.forEach((record: any) => {
      const id = getLogRecordId(record);
      if (id && apiSelectedIds.has(id)) idsToSelect.add(id);
    });
    setSelectedLogRecords(idsToSelect);
  }, [logData?.data?.data, selectedRecord?.welduid_s_event_torch, excludedTlogsData]);

  // Get records for a specific pass name
  const getRecordsForPass = (passName: string) => {
    if (!responseData) return [];
    
    const records = responseData?.data?.data?.records || responseData?.data?.records || responseData?.records;
    if (!records || !records[passName]) return [];
    
    return records[passName];
  };

  // Handle row click to show details table
  const handleRowClick = (passName: string) => {
    setSelectedPassName(passName);
    setShowDetailsTable(true);
  };

  // Handle back button to return to summary table
  const handleBackToSummary = () => {
    setShowDetailsTable(false);
    setSelectedPassName(null);
  };

  // Handle back button to return to records table
  const handleBackToRecords = () => {
    setShowLogTable(false);
    setLogData(null);
    setSelectedRecord(null);
    setSelectedLogRecords(new Set());
    setExcludedTlogsData([]);
  };

  // Unified back button handler - navigates back one step in the hierarchy
  const handleUnifiedBack = () => {
    // Priority: Log Table -> Details Table -> Summary Table -> Create PQR Mode -> Existing Reports -> Close Modal
    if (showLogTable) {
      // Go back from Log Table to Details Table
      handleBackToRecords();
    } else if (showDetailsTable) {
      // Go back from Details Table to Summary Table
      handleBackToSummary();
    } else if (showCreatePqrMode) {
      // Go back from Create PQR mode (summary table) to Existing Reports (do NOT reopen Create PQR modal)
      setShowCreatePqrMode(false);
      setShowExistingReports(true);
      setIsSuccess(false);
      setError(null);
      setResponseData(null);
      setToleranceUsedInLastGenerate(null);
      setCurrentPqrReportUrl(null);
      setCurrentPqrNumber(null);
      setShowDetailsTable(false);
      setShowLogTable(false);
      setLogData(null);
      setSelectedRecord(null);
      setSelectedLogRecords(new Set());
      setExcludedTlogsData([]);
      setEditedSummaryValues({});
      setShowCreatePqrModal(false);
    } else if (isSuccess) {
      // Go back from Summary Table to Existing PQR reports (e.g. after clicking eye on existing row)
      handleBackToExistingReports();
    } else {
      // Close modal (from Existing Reports view)
      onClose();
    }
  };

  // Handle download of PQR report (from existing row, responseData after generate, or existingReportInfo)
  const getCurrentPqrReportUrl = () => {
    return (
      currentPqrReportUrl ??
      responseData?.data?.data?.pqr_report_url ??
      responseData?.data?.pqr_report_url ??
      responseData?.pqr_report_url ??
      responseData?.data?.report_url ??
      existingReportInfo?.data?.pqrReport?.pqr_report_url
    );
  };

  // If report URL isn't ready yet, poll reports-info every 10 seconds until it becomes available.
  useEffect(() => {
    if (!isOpen) return;
    if (showExistingReports) return;
    if (!(isSuccess || showDetailsTable || showLogTable)) return;
    if (!projectId) return;
    if (effectiveSelectedWeldNumbers.length === 0) return;

    const url = getCurrentPqrReportUrl();
    if (url) return;

    fetchExistingReportsInfo();
    const intervalId = window.setInterval(() => {
      fetchExistingReportsInfo();
    }, 10_000);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    showExistingReports,
    isSuccess,
    showDetailsTable,
    showLogTable,
    projectId,
    effectiveSelectedWeldNumbers.join(','),
    tolerance,
    currentPqrReportUrl,
    responseData,
    existingReportInfo,
  ]);

  const handleDownloadReport = async () => {
    const reportUrl = getCurrentPqrReportUrl();
    if (!reportUrl) return;
    const filename = reportUrl.split('/').pop()?.split('?')[0] || 'pqr_report.xlsx';
    try {
      const token = await getJwtToken();
      const response = await fetch(reportUrl, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab (user can save from browser)
      const link = document.createElement('a');
      link.href = reportUrl;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleShareReport = () => {
    const reportUrl = getCurrentPqrReportUrl();
    if (reportUrl) {
      if (navigator.share) {
        navigator.share({
          title: 'PQR Report',
          url: reportUrl,
        }).catch(() => {
          navigator.clipboard?.writeText(reportUrl);
        });
      } else {
        navigator.clipboard?.writeText(reportUrl);
      }
    }
  };

  // Call exclude-tlogs API when tlogs are selected
  const excludeTlogs = async () => {
    if (!logData || !logData.data?.data || !Array.isArray(logData.data.data)) {
      return;
    }

    if (selectedLogRecords.size === 0) {
      return;
    }

    if (!projectId || !selectedRecord) {
      return;
    }

    setIsExcludingTlogs(true);
    try {
      const logRecords = logData.data.data;
      const boundary = parseInt(tolerance.replace('%', '')) || 10;

      // Get selected records
      const selectedRecords = Array.from(selectedLogRecords).map(idx => logRecords[idx]);

      // Group selected records by pass_name, torch, and weld_number
      const groupedRecords: { [key: string]: any[] } = {};
      
      selectedRecords.forEach((record: any) => {
        const m500ZoneData = extractM500ZoneDetailsFromData(record);
        const passName = record.pass_name || m500ZoneData.PassNum || '';
        const torch = record.torch || selectedRecord.torch || '';
        const weldNumber = effectiveSelectedWeldNumbers.join(',') || '';
        
        // Create a unique key for grouping
        const groupKey = `${passName}_${torch}_${weldNumber}`;
        
        if (!groupedRecords[groupKey]) {
          groupedRecords[groupKey] = [];
        }
        groupedRecords[groupKey].push(record);
      });

      // Build excluded_tlogs array
      const excludedTlogsArray = Object.values(groupedRecords).map((group: any[]) => {
        const firstRecord = group[0];
        const m500ZoneData = extractM500ZoneDetailsFromData(firstRecord);
        const passName = firstRecord.pass_name || m500ZoneData.PassNum || '';
        const torch = firstRecord.torch || selectedRecord.torch || '';
        const weldUuid = selectedRecord.welduid_s_event_torch || '';

        // Format weld_number - keep "W" prefix (e.g., "W001")
        // Try to get from record, or construct from selectedWeldAndJobNumbers
        let weldNumber = '';
        if (firstRecord.weld_number) {
          weldNumber = firstRecord.weld_number;
        } else if (selectedWeldAndJobNumbers.length > 0) {
          // Extract weld number from "W001:J123" format
          const weldJobPair = selectedWeldAndJobNumbers[0];
          if (weldJobPair.includes(':')) {
            const [weld] = weldJobPair.split(':');
            weldNumber = weld; // Keep "W001" format
          } else {
            weldNumber = weldJobPair;
          }
        } else if (effectiveSelectedWeldNumbers.length > 0) {
          weldNumber = effectiveSelectedWeldNumbers[0]; // Keep "W001" format
        }

        // Extract tlog IDs from records - using event_id, id, or time as fallback
        const excludedTlogIds = group.map((rec: any) => {
          // Try different possible ID fields
          if (rec.event_id) return String(rec.event_id);
          if (rec.id) return String(rec.id);
          if (rec.tlog_id) return String(rec.tlog_id);
          if (rec.time) return String(rec.time);
          return null;
        }).filter(Boolean) as string[];

        return {
          excluded_tlogs: excludedTlogIds,
          pass_name: passName,
          torch: torch,
          weld_number: weldNumber,
          weld_uuid: weldUuid,
        };
      });

      // Store excluded_tlogs in format needed for generate-pqr API (without project_id and weld_numbers)
      const excludedTlogsForGenerate = excludedTlogsArray.map((item: any) => ({
        pass_name: item.pass_name,
        torch: item.torch,
        weld_number: item.weld_number,
        excluded_tlogs: item.excluded_tlogs,
        weld_uuid: item.weld_uuid,
      }));
      setExcludedTlogsData(excludedTlogsForGenerate);

      // Format weld_numbers for the API (comma-separated, without W prefix)
      const weldNumbersForApi = effectiveSelectedWeldNumbers
        .map(weld => weld.replace(/^W/i, ''))
        .join(',');

      const payload = {
        project_id: projectId,
        weld_numbers: weldNumbersForApi,
        boundary: boundary,
        excluded_tlogs: excludedTlogsArray,
      };
      const baseUrl = process.env.REACT_APP_LIMITS_API_BASE_URL;

      const apiUrl = `${baseUrl}/pqr/exclude-tlogs`;
      const token = await getJwtToken();

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Exclude tlogs response:', data);
      
      // Return the excluded_tlogs data for use in generate-pqr
      return excludedTlogsForGenerate;
    } catch (err: any) {
      console.error('Error excluding tlogs:', err);
      // Don't set error state here, just log it - let generatePqrReport handle errors
      throw err; // Re-throw so generatePqrReport can handle it
    } finally {
      setIsExcludingTlogs(false);
    }
  };


  // Handle row click in records table to fetch log data
  const handleRecordRowClick = async (record: any) => {
    if (!record.welduid_s_event_torch || !projectId) return;

    setIsLoadingLog(true);
    setSelectedRecord(record);
    setError(null);

    try {
      // Build API URL
      const encodedProjectName = encodeURIComponent(projectName || project?.name || 'Test#001');
      const welduid_s_event_torch = encodeURIComponent(record.welduid_s_event_torch);
      const baseUrl = process.env.REACT_APP_LIMITS_API_BASE_URL;
      const apiUrl = `${baseUrl}/log?project_id=${projectId}&project_name=${encodedProjectName}&welduid_s_event_torch=${welduid_s_event_torch}`;

      const token = await getJwtToken();
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setLogData(data);
      setShowLogTable(true);
    } catch (err: any) {
      console.error('Error fetching log data:', err);
      setError(err.message || 'Failed to fetch log data');
    } finally {
      setIsLoadingLog(false);
    }
  };

  // Get column names from records (excluding welduid_s_event_torch) in a specific order
  const getRecordColumns = (records: any[]) => {
    if (!records || records.length === 0) return [];
    
    const firstRecord = records[0];
    // If records contain min*/max* keys (ex: minVoltage/maxVoltage), we display them inside the main column
    // (averageVoltage / arcEnergy etc.) to avoid an excessively wide table.
    const minMaxGroupedColumns: Record<
      string,
      { minKey: string; maxKey: string }
    > = {
      preHeatInterPassTemperature: { minKey: 'minPreHeatInterPassTemperature', maxKey: 'maxPreHeatInterPassTemperature' },
      averageVoltage: { minKey: 'minVoltage', maxKey: 'maxVoltage' },
      averageCurrent: { minKey: 'minCurrent', maxKey: 'maxCurrent' },
      averageWireFeedSpeed: { minKey: 'minWireFeedSpeed', maxKey: 'maxWireFeedSpeed' },
      averageOscillationWidth: { minKey: 'minOscillationWidth', maxKey: 'maxOscillationWidth' },
      averageOscillationFrequency: { minKey: 'minOscillationFrequency', maxKey: 'maxOscillationFrequency' },
      averageTravelSpeed: { minKey: 'minTravelSpeed', maxKey: 'maxTravelSpeed' },
      arcEnergy: { minKey: 'minArcEnergy', maxKey: 'maxArcEnergy' },
      averagePulseFrequency: { minKey: 'minPulseFrequency', maxKey: 'maxPulseFrequency' },
      averagePulseTime: { minKey: 'minPulseTime', maxKey: 'maxPulseTime' },
      averagePeakCurrent: { minKey: 'minPeakCurrent', maxKey: 'maxPeakCurrent' },
      averageBaseCurrent: { minKey: 'minBaseCurrent', maxKey: 'maxBaseCurrent' },
    };
    const minMaxHiddenKeys = new Set<string>();
    Object.values(minMaxGroupedColumns).forEach(({ minKey, maxKey }) => {
      minMaxHiddenKeys.add(minKey);
      minMaxHiddenKeys.add(maxKey);
    });

    const allKeys = Object.keys(firstRecord).filter((key) => {
      if (key === 'welduid_s_event_torch') return false;
      if (key === 'station_number' || key === 'stationNumber') return false;
      if (minMaxHiddenKeys.has(key)) return false;
      return true;
    });
    
    // Define the desired column order based on actual API response field names
    const orderedColumnPatterns = [
      ['passName'],
      ['weldNumber'],
      ['torch'],
      ['bugType'],
      ['preHeatInterPassTemperature'],
      ['averageVoltage'],
      ['averageWireFeedSpeed'],
      ['averageOscillationWidth'],
    ];
    
    // Separate ordered columns and remaining columns
    const ordered: (string | undefined)[] = new Array(orderedColumnPatterns.length);
    const remaining: string[] = [];
    const matchedKeys = new Set<string>();
    
    // Match keys to ordered patterns (exact match)
    allKeys.forEach(key => {
      let matched = false;
      
      orderedColumnPatterns.forEach((patterns, index) => {
        // Exact match with the first pattern in each array
        const isMatch = patterns[0] === key;
        
        if (isMatch && !ordered[index] && !matchedKeys.has(key)) {
          ordered[index] = key;
          matchedKeys.add(key);
          matched = true;
        }
      });
      
      if (!matched) {
        remaining.push(key);
      }
    });
    
    // Filter out undefined values and combine ordered + remaining
    let cols = ordered.filter((col): col is string => col !== undefined).concat(remaining);
    
    // If weldNumber contains "|" (weldNumber|jobNumber) in any record, insert jobNumber column after weldNumber
    const weldNumberKey = cols.find(c => c === 'weldNumber' || c === 'weld_number');
    const hasWeldJobPipe = weldNumberKey && records.some((r: any) => {
      const val = r[weldNumberKey] ?? r.weldNumber ?? r.weld_number;
      return typeof val === 'string' && val.includes('|');
    });
    if (hasWeldJobPipe && weldNumberKey) {
      const weldIdx = cols.indexOf(weldNumberKey);
      if (weldIdx !== -1 && !cols.includes('jobNumber')) {
        cols = [...cols.slice(0, weldIdx + 1), 'jobNumber', ...cols.slice(weldIdx + 1)];
      }
    }
    
    // Exclude passName / pass_name as we show it in the header
    cols = cols.filter(c => c !== 'passName' && c !== 'pass_name');
    
    return cols;
  };

  // Helper function to format values with fixed decimal places
  const toFixedValue = (value: any, digits: number = 2): string => {
    if (value === undefined || value === null) return '0';
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) return '0';
    return numValue.toFixed(digits);
  };

  const weldSummaryDisplayUnit = getUnitFromSettings();

  // Transform response data similar to reference structure
  const createDataFromPqrReport = useMemo(() => {
    if (!responseData) return [];

    // Extract summaryTable from API response: responseData.data.data.summaryTable
    const summaryTable = responseData?.data?.data?.summaryTable || responseData?.data?.summaryTable || responseData?.summaryTable;

    if (!summaryTable || typeof summaryTable !== 'object') {
      return [];
    }

    // Get all pass names (keys of summaryTable)
    const passes = Object.keys(summaryTable);

    return passes.map((pass) => {
      const passData = summaryTable[pass] || {};
      
      // Extract wpsLimitMin / wpsLimitMax from summaryTable structure.
      // If user selects MinOfAvg/MaxOfAvg, try common "avg" limit key variants (fallback to default keys).
      const pickLimitObj = (keys: string[]) => {
        for (const k of keys) {
          const v = (passData as any)?.[k];
          if (v && typeof v === 'object') return v;
        }
        return {};
      };

      const wpsLimitMin =
        pqrLimitsMode === 'avg'
          ? pickLimitObj(['wpsLimitMinofavg'])
          : pickLimitObj(['wpsLimitMinofmin']);
      const wpsLimitMax =
        pqrLimitsMode === 'avg'
          ? pickLimitObj(['wpsLimitMaxofavg'])
          : pickLimitObj(['wpsLimitMaxofmax', ]);

      console.log('wpsLimitMin', wpsLimitMin);
      console.log('wpsLimitMax', wpsLimitMax);

      const convWeldLimit = (raw: unknown, param: 'wireSpeed' | 'travelSpeed' | 'oscillationWidth', digits: number) => {
        const numValue = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
        if (isNaN(numValue)) return toFixedValue(0, digits);
        return toFixedValue(convertWeldDataValue(numValue, param), digits);
      };

      return {
        key: pass,
        passName: pass,
        preHeatMin: toFixedValue(wpsLimitMin.preHeatInterPassTemperature),
        preHeatMax: toFixedValue(wpsLimitMax.preHeatInterPassTemperature),
        voltsMin: toFixedValue(wpsLimitMin.averageVoltage),
        voltsMax: toFixedValue(wpsLimitMax.averageVoltage),
        ampsMin: toFixedValue(wpsLimitMin.averageCurrent),
        ampsMax: toFixedValue(wpsLimitMax.averageCurrent),
        minWireFeedSpeed: convWeldLimit(wpsLimitMin.averageWireFeedSpeed, 'wireSpeed', 0),
        maxWireFeedSpeed: convWeldLimit(wpsLimitMax.averageWireFeedSpeed, 'wireSpeed', 0),
        oscWidthMin: convWeldLimit(wpsLimitMin.averageOscillationWidth, 'oscillationWidth', 2),
        oscWidthMax: convWeldLimit(wpsLimitMax.averageOscillationWidth, 'oscillationWidth', 2),
        oscFrqMin: toFixedValue(wpsLimitMin.averageOscillationFrequency),
        oscFrqMax: toFixedValue(wpsLimitMax.averageOscillationFrequency),
        travelSpeedMin: convWeldLimit(wpsLimitMin.averageTravelSpeed, 'travelSpeed', 1),
        travelSpeedMax: convWeldLimit(wpsLimitMax.averageTravelSpeed, 'travelSpeed', 1),
        arcEnergyMin: toFixedValue(wpsLimitMin.arcEnergy),
        arcEnergyMax: toFixedValue(wpsLimitMax.arcEnergy),
        pulseFreqMin: toFixedValue(wpsLimitMin.averagePulseFrequency),
        pulseFreqMax: toFixedValue(wpsLimitMax.averagePulseFrequency),
        pulseTimeMin: toFixedValue(wpsLimitMin.averagePulseTime),
        pulseTimeMax: toFixedValue(wpsLimitMax.averagePulseTime),
        peakCurrentMin: toFixedValue(wpsLimitMin.averagePeakCurrent),
        peakCurrentMax: toFixedValue(wpsLimitMax.averagePeakCurrent),
        baseCurrentMin: toFixedValue(wpsLimitMin.averageBaseCurrent),
        baseCurrentMax: toFixedValue(wpsLimitMax.averageBaseCurrent),
      };
    });
  }, [responseData, pqrLimitsMode, weldSummaryDisplayUnit]);

  // Column definitions matching PqrSummaryTable structure
  // Set `editable: true` for columns that should have editable input fields in the summary table
  // The `editable` property is used throughout the rendering logic to conditionally render input fields
  const columnDefinitions = useMemo(() => {
    const sfx = (suffixKey: string) => getParameterUnitSuffix(suffixKey);
    return [
      { key: 'preHeat', label: 'Pre-Heat/ Interpass Temperature (°C)', minKey: 'preHeatMin', maxKey: 'preHeatMax', editable: true, icon: Thermometer, showAvg: false },
      { key: 'volts', label: 'Voltage (V)', minKey: 'voltsMin', maxKey: 'voltsMax', editable: false, icon: Zap, showAvg: true },
      { key: 'amps', label: 'Current (A)', minKey: 'ampsMin', maxKey: 'ampsMax', editable: false, icon: Zap, showAvg: true },
      { key: 'wireFeedSpeed', label: `Wire Feed Speed (${sfx('wire_speed')})`, minKey: 'minWireFeedSpeed', maxKey: 'maxWireFeedSpeed', editable: false, icon: Gauge, showAvg: true },
      { key: 'oscWidth', label: `Oscillation Width (${sfx('oscillation_width')})`, minKey: 'oscWidthMin', maxKey: 'oscWidthMax', editable: false, icon: Wifi, showAvg: true },
      { key: 'oscFrq', label: 'Oscillation Frequency (Hz)', minKey: 'oscFrqMin', maxKey: 'oscFrqMax', editable: false, icon: Activity, showAvg: true },
      { key: 'travelSpeed', label: `Travel Speed (${sfx('travel_speed')})`, minKey: 'travelSpeedMin', maxKey: 'travelSpeedMax', editable: false, icon: Gauge, showAvg: true },
      { key: 'arcEnergy', label: `Arc Energy (${sfx('heat')})`, minKey: 'arcEnergyMin', maxKey: 'arcEnergyMax', editable: true, icon: Activity, showAvg: false },
      { key: 'pulseFreq', label: 'Pulse Frequency (Hz)', minKey: 'pulseFreqMin', maxKey: 'pulseFreqMax', editable: true, icon: Activity, showAvg: true },
      { key: 'pulseTime', label: 'Pulse Time (ms)', minKey: 'pulseTimeMin', maxKey: 'pulseTimeMax', editable: true, icon: Gauge, showAvg: true },
      { key: 'peakCurrent', label: 'Peak Current (A)', minKey: 'peakCurrentMin', maxKey: 'peakCurrentMax', editable: true, icon: Zap, showAvg: true },
      { key: 'baseCurrent', label: 'Base Current (A)', minKey: 'baseCurrentMin', maxKey: 'baseCurrentMax', editable: true, icon: Zap, showAvg: true },
    ];
  }, [weldSummaryDisplayUnit]);

  // Helper function to render log data table
  const renderLogTable = () => {
    if (!logData || !logData.data?.data || !Array.isArray(logData.data.data)) {
      return (
        <div className="h-full flex flex-col w-full overflow-hidden">
          <div className="flex-1 flex items-center justify-center min-h-[200px]">
            {isLoadingLog ? (
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
                <span className="text-gray-600 text-lg">Loading log data...</span>
              </div>
            ) : (
              <span className="text-gray-500">No log data available</span>
            )}
          </div>
        </div>
      );
    }

    const logRecords = logData.data.data;

    type LogConvertParam = 'travelSpeed' | 'wireSpeed' | 'heat' | 'trueEnergy' | 'oscillationWidth' | 'totalWireConsumed' | 'distance';

    const unitSfx = (suffixKey: string) => getParameterUnitSuffix(suffixKey);

    const columns: Array<{
      key: string;
      label: string;
      icon?: any;
      recordField?: string;
      convertParam?: LogConvertParam;
      decimals?: number;
    }> = [
      { key: 'slno', label: 'Sl.no', icon: Hash },
      { key: 'event', label: 'event', icon: Activity },
      { key: 'job_number', label: 'Job Number', icon: FileText },
      { key: 'time', label: 'Time', icon: Activity },
      { key: 'pipe_name', label: 'Pipe', icon: Target },
      { key: 'pass', label: 'Pass', icon: FileText },
      { key: 'zone', label: 'Zone', icon: Target },
      { key: 'travel_speed', label: `Travel Speed (${unitSfx('travel_speed')})`, icon: Gauge, recordField: 'travel_speed', convertParam: 'travelSpeed', decimals: 1 },
      { key: 'wire_speed', label: `Wire Speed (${unitSfx('wire_speed')})`, icon: Gauge, recordField: 'wire_speed', convertParam: 'wireSpeed', decimals: 0 },
      { key: 'volts', label: 'Volt (V)', icon: Zap },
      { key: 'current', label: 'Current (A)', icon: Zap },
      { key: 'heat', label: formatHeatInputLabel(unitSfx('heat')), icon: Thermometer, recordField: 'heat', convertParam: 'heat', decimals: 2 },
      { key: 'true_energy', label: `True Energy (${unitSfx('true_energy')})`, icon: Activity, recordField: 'true_energy', convertParam: 'trueEnergy', decimals: 2 },
      { key: 'oscillation_width', label: `Oscillation Width (${unitSfx('oscillation_width')})`, icon: Wifi, recordField: 'oscillation_width', convertParam: 'oscillationWidth', decimals: 2 },
      { key: 'total_wire_consumed', label: `Total Wire Consumed (${unitSfx('total_wire_consumed')})`, icon: Gauge, recordField: 'total_wire_consumed', convertParam: 'totalWireConsumed', decimals: 0 },
      { key: 'distance', label: `Distance (${unitSfx('logging_distance')})`, icon: Activity, recordField: 'distance', convertParam: 'distance', decimals: 2 },
      { key: 'head_angle', label: 'Head Angle (deg)', icon: Activity },
      { key: 'arc_impedance', label: 'Arc Impedance (ohms)', icon: Wifi },
    ];

    // Min/Avg/Max in header (same pattern as WeldPassDetailTable)
    const calculateStats = (field: string, decimals: number) => {
      let min = Infinity;
      let max = -Infinity;
      let sum = 0;
      let count = 0;

      for (let i = 0; i < logRecords.length; i++) {
        const val = (logRecords[i] as any)?.[field];
        if (val !== null && val !== undefined && !isNaN(val)) {
          const numVal = Number(val);
          min = Math.min(min, numVal);
          max = Math.max(max, numVal);
          sum += numVal;
          count++;
        }
      }

      if (count === 0) return { min: '-', avg: '-', max: '-' };
      return {
        min: min.toFixed(decimals),
        avg: (sum / count).toFixed(decimals),
        max: max.toFixed(decimals),
      };
    };

    const pickLogNumericValue = (record: any, field: string): number | null => {
      const keyGroups: Record<string, readonly string[]> = {
        travel_speed: ['travel_speed', 'travelSpeed'],
        wire_speed: ['wire_speed', 'wireSpeed'],
        true_energy: ['true_energy', 'trueEnergy'],
        oscillation_width: ['oscillation_width', 'oscillationWidth'],
        total_wire_consumed: ['total_wire_consumed', 'totalWireConsumed'],
        distance: ['distance', 'logging_distance'],
      };
      const keys = keyGroups[field] ?? [field];
      for (const k of keys) {
        const v = record[k];
        if (v !== null && v !== undefined && v !== '' && !isNaN(Number(v))) {
          return Number(v);
        }
      }
      return null;
    };

    const calculateStatsConverted = (field: string, decimals: number, convertParam: LogConvertParam) => {
      let min = Infinity;
      let max = -Infinity;
      let sum = 0;
      let count = 0;

      for (let i = 0; i < logRecords.length; i++) {
        const numVal = pickLogNumericValue(logRecords[i] as any, field);
        if (numVal !== null) {
          const disp = convertWeldDataValue(numVal, convertParam);
          min = Math.min(min, disp);
          max = Math.max(max, disp);
          sum += disp;
          count++;
        }
      }

      if (count === 0) return { min: '-', avg: '-', max: '-' };
      return {
        min: min.toFixed(decimals),
        avg: (sum / count).toFixed(decimals),
        max: max.toFixed(decimals),
      };
    };

    const parameterStats: Record<string, { min: string; avg: string; max: string }> = {
      travel_speed: calculateStatsConverted('travel_speed', 1, 'travelSpeed'),
      wire_speed: calculateStatsConverted('wire_speed', 0, 'wireSpeed'),
      volts: calculateStats('volts', 1),
      current: calculateStats('current', 0),
      heat: calculateStatsConverted('heat', 2, 'heat'),
      true_energy: calculateStatsConverted('true_energy', 2, 'trueEnergy'),
      oscillation_width: calculateStatsConverted('oscillation_width', 2, 'oscillationWidth'),
      total_wire_consumed: calculateStatsConverted('total_wire_consumed', 0, 'totalWireConsumed'),
      distance: calculateStatsConverted('distance', 2, 'distance'),
      head_angle: calculateStats('head_angle', 2),
      arc_impedance: calculateStats('arc_impedance', 2),
    };

    const formatLogConvertedCell = (record: any, field: string, decimals: number, convertParam: LogConvertParam) => {
      const numVal = pickLogNumericValue(record, field);
      if (numVal === null) return '-';
      const converted = convertWeldDataValue(numVal, convertParam);
      return typeof converted === 'number' ? converted.toFixed(decimals) : '-';
    };

    const renderActualHeaderValues = (paramName: string) => {
      const stats = parameterStats[paramName];
      if (!stats || stats.min === '-' || stats.avg === '-' || stats.max === '-') {
        return (
          <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
            <div>Min: -</div>
            <div>Avg: -</div>
            <div>Max: -</div>
          </div>
        );
      }

      return (
        <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
          <div>Min: {stats.min}</div>
          <div>Avg: {stats.avg}</div>
          <div>Max: {stats.max}</div>
        </div>
      );
    };

    const normalizeWeld = (w: unknown) => String(w ?? '').trim().replace(/^W/i, '');
    const rawWeld = selectedRecord?.weldNumber ?? selectedRecord?.weld_number ?? '';
    const weldNum = typeof rawWeld === 'string' && rawWeld.includes('|') ? rawWeld.split('|')[0].trim() : rawWeld;
    const weldNumNorm = normalizeWeld(weldNum);

    const passNameForExcluded = selectedRecord?.passName ?? selectedRecord?.pass_name ?? '';
    const torchForExcluded = selectedRecord?.torch ?? '';
    const weldUuidForExcluded = selectedRecord?.welduid_s_event_torch ?? '';

    const updateExcludedTlogsForCurrentWeld = (tlogId: string, shouldBeExcluded: boolean) => {
      if (!tlogId) return;
      setExcludedTlogsData((prev: any[]) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex((item: any) => normalizeWeld(item?.weld_number) === weldNumNorm);
        const base: any =
          idx >= 0
            ? { ...next[idx] }
            : {
                pass_name: passNameForExcluded,
                torch: torchForExcluded,
                weld_number: weldNumNorm,
                weld_uuid: weldUuidForExcluded,
                excluded_tlogs: [],
              };

        const set = new Set<string>((base.excluded_tlogs ?? []).map((x: any) => String(x)));
        if (shouldBeExcluded) set.add(tlogId);
        else set.delete(tlogId);

        base.excluded_tlogs = Array.from(set);
        base.pass_name = base.pass_name || passNameForExcluded;
        base.torch = base.torch || torchForExcluded;
        base.weld_uuid = base.weld_uuid || weldUuidForExcluded;
        base.weld_number = base.weld_number || weldNumNorm;

        if (base.excluded_tlogs.length === 0) {
          if (idx >= 0) next.splice(idx, 1);
          return next;
        }

        if (idx >= 0) next[idx] = base;
        else next.push(base);
        return next;
      });
    };

    // Handle checkbox change for individual row (checked = excluded)
    const handleLogRecordCheckboxChange = (tlogId: string, checked: boolean) => {
      if (!tlogId) return;
      const newSelected = new Set(selectedLogRecords);
      if (checked) newSelected.add(tlogId);
      else newSelected.delete(tlogId);
      setSelectedLogRecords(newSelected);
      updateExcludedTlogsForCurrentWeld(tlogId, checked);
    };

    const logRecordIds = logRecords.map((r: any) => getLogRecordId(r)).filter(Boolean) as string[];

    // Handle select all checkbox (checked = exclude all)
    const handleSelectAllLogRecords = (checked: boolean) => {
      if (checked) {
        const allIds = new Set<string>(logRecordIds);
        setSelectedLogRecords(allIds);
        logRecordIds.forEach((id) => updateExcludedTlogsForCurrentWeld(id, true));
      } else {
        setSelectedLogRecords(new Set());
        logRecordIds.forEach((id) => updateExcludedTlogsForCurrentWeld(id, false));
      }
    };

    const allSelected = logRecordIds.length > 0 && logRecordIds.every((id) => selectedLogRecords.has(id));

    return (
      <div className="h-full flex flex-col w-full overflow-hidden rounded-none border-0 border-gray-200 sm:rounded-none">
        <div className="flex-1 relative overflow-hidden min-h-0">
          <div
            className="absolute inset-0 overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}
          >
            <Table className="w-full border-collapse text-sm" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
            <TableHeader className="bg-page-bg sticky top-0 z-30">
              <TableRow className="border-b">
                <TableHead
                  className="px-3 py-2 text-center font-semibold text-gray-600 border-r border-[#E2E5EA] whitespace-nowrap"
                  style={{ minWidth: '50px', width: '50px' }}
                >
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAllLogRecords}
                    className="w-4 h-4"
                  />
                </TableHead>
                {columns.map((col, idx) => {
                  const IconComponent = col.icon;
                  const showStats = Boolean(parameterStats[col.key]);
                  return (
                    <TableHead
                      key={idx}
                      className="px-3 py-2 text-center font-semibold text-gray-500 border-r border-[#E2E5EA] whitespace-nowrap"
                      style={{ minWidth: '100px' }}
                    >
                      <div className="flex flex-col items-center justify-center gap-1">
                        <div className="flex items-center justify-center gap-1.5">
                          {IconComponent && <IconComponent className="h-4 w-4" />}
                          <span>{col.label}</span>
                        </div>
                        {showStats ? renderActualHeaderValues(col.key) : null}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {logRecords.map((record: any, recordIdx: number) => {
                // console.log('record.time....',record.time)
                // Format time from timestamp (only time, no date)
                const timeValue = record.time ? formatDateTime(record.time * 1000) : '-';
                
                // Extract M500 zone details
                const m500ZoneData = extractM500ZoneDetailsFromData(record);
                
                const rowId = getLogRecordId(record);
                const isSelected = rowId ? selectedLogRecords.has(rowId) : false;
                
                return (
                  <TableRow
                    key={recordIdx}
                    className={`h-[52px] bg-white ${recordIdx < logRecords.length - 1 ? 'border-b border-[#EEF0F3]' : ''} hover:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <TableCell className="px-3 py-2 text-center border-r border-[#E2E5EA] whitespace-nowrap">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handleLogRecordCheckboxChange(rowId, checked as boolean)}
                        className="w-4 h-4"
                      />
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {recordIdx + 1}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {record.event || '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {record.job_number || '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {timeValue}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {record.pipe_name || '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {m500ZoneData.PassNum || record.pass_name || '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {m500ZoneData.ZoneNum || record.zone_name || '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {formatLogConvertedCell(record, 'travel_speed', 1, 'travelSpeed')}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-gray-200 whitespace-nowrap text-xs">
                      {formatLogConvertedCell(record, 'wire_speed', 0, 'wireSpeed')}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {record.volts !== undefined ? toFixedValue(record.volts, 1) : '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {record.current !== undefined ? toFixedValue(record.current, 0) : '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {formatLogConvertedCell(record, 'heat', 2, 'heat')}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-gray-200 whitespace-nowrap text-xs">
                      {formatLogConvertedCell(record, 'true_energy', 2, 'trueEnergy')}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-gray-200 whitespace-nowrap text-xs">
                      {formatLogConvertedCell(record, 'oscillation_width', 2, 'oscillationWidth')}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-gray-200 whitespace-nowrap text-xs">
                      {formatLogConvertedCell(record, 'total_wire_consumed', 0, 'totalWireConsumed')}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-gray-200 whitespace-nowrap text-xs">
                      {formatLogConvertedCell(record, 'distance', 2, 'distance')}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {record.head_angle !== undefined ? toFixedValue(record.head_angle, 2) : '-'}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                      {record.arc_impedance !== undefined ? toFixedValue(record.arc_impedance, 2) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        </div>
      </div>
    );
  };

  // Configuration for record columns - maps API field names to display properties
  const recordColumnConfig = useMemo(() => {
    const sfx = (suffixKey: string) => getParameterUnitSuffix(suffixKey);
    return [
      { key: 'passName', label: 'Pass Name', icon: Target, showAvg: false },
      { key: 'weldNumber', label: 'Weld Number', icon: Target, showAvg: false },
      { key: 'jobNumber', label: 'Job Number', icon: FileText, showAvg: false },
      { key: 'torch', label: 'Torch', icon: User, showAvg: false },
      { key: 'bugType', label: 'Bug Type', icon: Settings, showAvg: false },
      { key: 'preHeatInterPassTemperature', label: 'Pre-Heat/Interpass Temperature', icon: Thermometer, showAvg: false },
      { key: 'averageVoltage', label: 'Voltage (V)', icon: Zap, showAvg: true },
      { key: 'averageWireFeedSpeed', label: `Wire Feed Speed (${sfx('wire_speed')})`, icon: Gauge, showAvg: true },
      { key: 'average_wire_feed_speed', label: `Wire Feed Speed (${sfx('wire_speed')})`, icon: Gauge, showAvg: true },
      { key: 'averageOscillationWidth', label: `Oscillation Width (${sfx('oscillation_width')})`, icon: Wifi, showAvg: true },
      { key: 'average_oscillation_width', label: `Oscillation Width (${sfx('oscillation_width')})`, icon: Wifi, showAvg: true },
      { key: 'averageOscillationFrequency', label: 'Oscillation Frequency (Hz)', icon: Activity, showAvg: true },
      { key: 'averageTravelSpeed', label: `Travel Speed (${sfx('travel_speed')})`, icon: Gauge, showAvg: true },
      { key: 'average_travel_speed', label: `Travel Speed (${sfx('travel_speed')})`, icon: Gauge, showAvg: true },
      { key: 'arcEnergy', label: `Arc Energy (${sfx('heat')})`, icon: Activity, showAvg: false },
      { key: 'arc_energy', label: `Arc Energy (${sfx('heat')})`, icon: Activity, showAvg: false },
      { key: 'averagePulseFrequency', label: 'Pulse Frequency (Hz)', icon: Activity, showAvg: true },
      { key: 'averagePulseTime', label: 'Pulse Time (ms)', icon: Gauge, showAvg: true },
      { key: 'averagePeakCurrent', label: 'Peak Current (A)', icon: Zap, showAvg: true },
      { key: 'averageBaseCurrent', label: 'Base Current (A)', icon: Zap, showAvg: true },
      { key: 'averageCurrent', label: 'Current (A)', icon: Zap, showAvg: true },
    ];
  }, [weldSummaryDisplayUnit]);

  // Helper function to get icon and label for record columns
  const getRecordColumnInfo = (columnName: string): { icon: React.ComponentType<any> | null; label: string; showAvg: boolean } => {
    // Try exact match first
    const exactMatch = recordColumnConfig.find(config => config.key === columnName);
    if (exactMatch) {
      return { icon: exactMatch.icon, label: exactMatch.label, showAvg: exactMatch.showAvg };
    }
    
    // Try case-insensitive match
    const columnLower = columnName.toLowerCase();
    const caseInsensitiveMatch = recordColumnConfig.find(config => 
      config.key.toLowerCase() === columnLower
    );
    if (caseInsensitiveMatch) {
      return { icon: caseInsensitiveMatch.icon, label: caseInsensitiveMatch.label, showAvg: caseInsensitiveMatch.showAvg };
    }
    
    // Try partial match (for variations like pass_name, weld_number, etc.)
    const partialMatch = recordColumnConfig.find(config => {
      const configKeyLower = config.key.toLowerCase();
      return columnLower.includes(configKeyLower) || configKeyLower.includes(columnLower);
    });
    if (partialMatch) {
      return { icon: partialMatch.icon, label: partialMatch.label, showAvg: partialMatch.showAvg };
    }
    
    // Default: format the column name nicely
    return { icon: null, label: columnName.split(/(?=[A-Z])/).join(' '), showAvg: false };
  };

  // Helper function to render details table for records
  const renderDetailsTable = () => {
    if (!selectedPassName) return null;

    const records = getRecordsForPass(selectedPassName);
    const recordColumns = getRecordColumns(records);
    const isMinMaxMode = pqrLimitsMode === 'minmax';

    type DetailConvertParam = 'wireSpeed' | 'travelSpeed' | 'oscillationWidth' | 'heat';
    const getDetailConvertParam = (columnKey: string): DetailConvertParam | undefined => {
      if (/averageWireFeedSpeed|average_wire_feed_speed/i.test(columnKey)) return 'wireSpeed';
      if (/averageOscillationWidth|average_oscillation_width/i.test(columnKey)) return 'oscillationWidth';
      if (/averageTravelSpeed|average_travel_speed/i.test(columnKey)) return 'travelSpeed';
      if (/^arcEnergy$|^arc_energy$/i.test(columnKey)) return 'heat';
      return undefined;
    };
    const getDetailDigits = (columnKey: string) => {
      const p = getDetailConvertParam(columnKey);
      if (p === 'wireSpeed') return 0;
      if (p === 'travelSpeed') return 1;
      return 2;
    };

    const formatDetailNumeric = (v: any, col: string, digits: number) => {
      if (v === undefined || v === null || v === '') return '-';
      const n = typeof v === 'number' ? v : parseFloat(String(v));
      if (isNaN(n)) return String(v);
      const p = getDetailConvertParam(col);
      if (p) {
        return toFixedValue(convertWeldDataValue(n, p), digits);
      }
      return toFixedValue(n, digits);
    };

    // When MinOfMin/MaxOfMax is selected, show parameter columns like the summary table:
    // each parameter becomes two columns (Min, Max) and we use min*/max* fields from API.
    const minMaxColumnMap: Record<string, { minKey: string; maxKey: string; digits: number }> = {
      preHeatInterPassTemperature: { minKey: 'minPreHeatInterPassTemperature', maxKey: 'maxPreHeatInterPassTemperature', digits: 0 },
      averageVoltage: { minKey: 'minVoltage', maxKey: 'maxVoltage', digits: 1 },
      averageCurrent: { minKey: 'minCurrent', maxKey: 'maxCurrent', digits: 0 },
      averageWireFeedSpeed: { minKey: 'minWireFeedSpeed', maxKey: 'maxWireFeedSpeed', digits: 0 },
      averageOscillationWidth: { minKey: 'minOscillationWidth', maxKey: 'maxOscillationWidth', digits: 2 },
      averageOscillationFrequency: { minKey: 'minOscillationFrequency', maxKey: 'maxOscillationFrequency', digits: 0 },
      averageTravelSpeed: { minKey: 'minTravelSpeed', maxKey: 'maxTravelSpeed', digits: 1 },
      arcEnergy: { minKey: 'minArcEnergy', maxKey: 'maxArcEnergy', digits: 2 },
      averagePulseFrequency: { minKey: 'minPulseFrequency', maxKey: 'maxPulseFrequency', digits: 0 },
      averagePulseTime: { minKey: 'minPulseTime', maxKey: 'maxPulseTime', digits: 0 },
      averagePeakCurrent: { minKey: 'minPeakCurrent', maxKey: 'maxPeakCurrent', digits: 0 },
      averageBaseCurrent: { minKey: 'minBaseCurrent', maxKey: 'maxBaseCurrent', digits: 0 },
    };

    const isMinMaxColumn = (col: string) => isMinMaxMode && Boolean(minMaxColumnMap[col]);

    if (records.length === 0) {
      return (
        <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
          {/* <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0"> */}
            {/* <h3 className="text-sm font-bold text-gray-600">Records for Pass: {selectedPassName}</h3> */}
          {/* </div> */}
          <div className="p-8 text-center text-gray-500">
            No records available for this pass
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col w-full overflow-hidden rounded-none border-0 border-gray-200 sm:rounded-none">
        <div className="flex-1 relative overflow-hidden min-h-0">
          <div
            className="absolute inset-0 overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}
          >
            <Table className="w-full border-collapse text-sm" style={{ minWidth: 'max-content', tableLayout: 'auto' }}>
            <TableHeader className="bg-page-bg sticky top-0 z-30">
              {isMinMaxMode ? (
                <>
                  {/* Header row 1: names */}
                  <TableRow className="border-b h-[72px]">
                    {recordColumns.map((col, idx) => {
                      const { icon: IconComponent, label } = getRecordColumnInfo(col);
                      const grouped = isMinMaxColumn(col);
                      return (
                        <React.Fragment key={idx}>
                          <TableHead
                            rowSpan={grouped ? 1 : 2}
                            colSpan={grouped ? 2 : 1}
                            className="px-2 text-center border-r border-[#E2E5EA] bg-page-bg align-middle"
                            style={{ minWidth: grouped ? '180px' : '180px' }}
                          >
                            <div className="flex flex-col items-center justify-center">
                              <div className="flex items-center justify-center gap-1.5">
                                {IconComponent && <IconComponent className="h-4 w-4" />}
                                <span>{label}</span>
                              </div>
                            </div>
                          </TableHead>
                          {idx === 0 && (
                            <TableHead
                              rowSpan={2}
                              className="px-3 py-2 text-center border-r border-[#E2E5EA] bg-page-bg whitespace-nowrap align-middle"
                              style={{ minWidth: '80px' }}
                            >
                              <div className="flex items-center justify-center gap-1.5">
                                <Settings className="h-4 w-4" />
                                <span>Action</span>
                              </div>
                            </TableHead>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableRow>

                  {/* Header row 2: Min/Max labels for grouped columns */}
                  <TableRow className="border-b">
                    {recordColumns.map((col, idx) => {
                      if (!isMinMaxColumn(col)) return null;
                      return (
                        <React.Fragment key={idx}>
                          <TableHead
                            className="px-2 py-2 text-center text-xs font-normal text-gray-500 border-r border-[#E2E5EA] bg-page-bg"
                            style={{ minWidth: '90px' }}
                          >
                            Min
                          </TableHead>
                          <TableHead
                            className="px-2 py-2 text-center text-xs font-normal text-gray-500 border-r border-[#E2E5EA] bg-page-bg"
                            style={{ minWidth: '90px' }}
                          >
                            Max
                          </TableHead>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                </>
              ) : (
                <TableRow className="border-b h-[52px]">
                  {recordColumns.map((col, idx) => {
                    const { icon: IconComponent, label, showAvg } = getRecordColumnInfo(col);
                    return (
                      <React.Fragment key={idx}>
                        <TableHead
                          className="px-2 text-center border-r border-[#E2E5EA] bg-page-bg"
                          style={{ minWidth: '180px' }}
                        >
                          <div className="flex flex-col items-center justify-center">
                            <div className="flex items-center justify-center gap-1.5">
                              {IconComponent && <IconComponent className="h-4 w-4" />}
                              <span>{label}</span>
                            </div>
                            {showAvg && (
                              <div className="text-xs font-normal text-gray-500 mt-0.5">Avg</div>
                            )}
                          </div>
                        </TableHead>
                        {idx === 0 && (
                          <TableHead
                            className="px-3 py-2 text-center border-r border-[#E2E5EA] bg-page-bg whitespace-nowrap"
                            style={{ minWidth: '80px' }}
                          >
                            <div className="flex items-center justify-center gap-1.5">
                              <Settings className="h-4 w-4" />
                              <span>Action</span>
                            </div>
                          </TableHead>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {records.map((record: any, recordIdx: number) => (
                <TableRow
                  key={recordIdx}
                  className={`h-[52px] bg-white ${recordIdx < records.length - 1 ? 'border-b border-[#EEF0F3]' : ''} transition-colors`}
                >
                  {recordColumns.map((col, colIdx) => {
                    // weldNumber may contain "weldNumber|jobNumber" - split into two columns
                    const rawWeld = record.weldNumber ?? record.weld_number ?? '';
                    const weldStr = typeof rawWeld === 'string' ? rawWeld : String(rawWeld || '');
                    const [weldPart, jobPart] = weldStr.includes('|') ? weldStr.split('|', 2) : [weldStr, ''];

                    // MinOfMin/MaxOfMax mode: parameter columns render as two cells (Min, Max) like summary table
                    if (isMinMaxMode && minMaxColumnMap[col]) {
                      const { minKey, maxKey, digits } = minMaxColumnMap[col];
                      const minVal = formatDetailNumeric(record[minKey], col, digits);
                      const maxVal = formatDetailNumeric(record[maxKey], col, digits);
                      return (
                        <React.Fragment key={colIdx}>
                          <TableCell className="px-3 py-2 text-center text-gray-600 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                            {minVal}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-center text-gray-600 border-r border-[#E2E5EA] whitespace-nowrap text-xs">
                            {maxVal}
                          </TableCell>
                          {colIdx === 0 && (
                            <TableCell className="px-3 py-2 text-center border-r border-[#E2E5EA] whitespace-nowrap">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                                title="View Log Data"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRecordRowClick(record);
                                }}
                              >
                                <Eye className="h-5 w-5 text-gray-600" />
                              </button>
                            </TableCell>
                          )}
                        </React.Fragment>
                      );
                    }

                    const defaultDigits = getDetailDigits(col);
                    let displayValue: string;
                    if (col === 'jobNumber') {
                      displayValue = jobPart.trim() || '-';
                    } else if (col === 'weldNumber' || col === 'weld_number') {
                      displayValue = weldPart.trim() || '-';
                    } else {
                      const raw = record[col];
                      if (raw === undefined || raw === null || raw === '') {
                        displayValue = '-';
                      } else if (getDetailConvertParam(col)) {
                        displayValue = formatDetailNumeric(raw, col, defaultDigits);
                      } else if (typeof raw === 'number') {
                        displayValue = toFixedValue(raw, defaultDigits);
                      } else {
                        const n = parseFloat(String(raw));
                        displayValue = isNaN(n) ? String(raw) : n.toFixed(defaultDigits);
                      }
                    }
                    return (
                    <React.Fragment key={colIdx}>
                      <TableCell
                        className="px-3 py-2 text-center text-gray-600 border-r border-[#E2E5EA] whitespace-nowrap text-xs"
                      >
                        {displayValue}
                      </TableCell>
                      {colIdx === 0 && (
                        <TableCell className="px-3 py-2 text-center border-r border-[#E2E5EA] whitespace-nowrap">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                            title="View Log Data"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRecordRowClick(record);
                            }}
                          >
                            <Eye className="h-5 w-5 text-gray-600" />
                          </button>
                        </TableCell>
                      )}
                    </React.Fragment>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to render summary table from transformed data
  const renderSummaryTable = () => {
    if (!responseData || createDataFromPqrReport.length === 0) {
      return (
        <div className="h-full flex flex-col w-full overflow-hidden">
          <div className="flex-1 flex items-center justify-center min-h-[200px]">
            <span className="text-gray-500">No data available</span>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col w-full overflow-hidden rounded-none border-0 border-gray-200 sm:rounded-none">
        <div className="flex-1 relative overflow-hidden min-h-0">
          <div
            className="absolute inset-0 overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }}
          >
            <Table className="w-full border-collapse text-sm" style={{ width: '100%', minWidth: 'max-content', tableLayout: 'auto' }}>
            <TableHeader className="bg-page-bg sticky top-0 z-30">
              {/* First header row - Parameter names with icons */}
              <TableRow className="border-b h-[72px]">
                <TableHead 
                  rowSpan={2}
                  className="px-3 py-2 text-center font-bold text-gray-600 border-r border-[#E2E5EA] whitespace-nowrap align-middle bg-gray-50"
                  style={{ minWidth: '120px', verticalAlign: 'middle' }}
                >
                  Pass Name
                </TableHead>
                <TableHead 
                  rowSpan={2}
                  className="px-3 py-2 text-center border-r border-[#E2E5EA] bg-page-bg whitespace-nowrap align-middle"
                  style={{ minWidth: '80px', verticalAlign: 'middle' }}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <Settings className="h-4 w-4" />
                    <span className="text-xs font-medium">Action</span>
                  </div>
                </TableHead>
                {columnDefinitions.map((column, index) => {
                  const IconComponent = column.icon;
                  return (
                    <TableHead
                      key={index}
                      colSpan={2}
                      className="px-2 text-center border-r border-[#E2E5EA] bg-page-bg"
                      style={{ minWidth: '180px' }}
                    >
                      <div className="h-[40px] flex flex-col items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          {IconComponent && <IconComponent className="h-4 w-4" />}
                          <span>{column.label}</span>
                        </div>
                        {column.showAvg && (
                          <div className="text-xs font-normal text-gray-500 mt-0.5">Avg</div>
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
              {/* Second header row - Min/Max labels */}
              <TableRow className="border-b">
                {columnDefinitions.map((column, index) => (
                  <React.Fragment key={index}>
                    <TableHead 
                      className="px-2 py-2 text-center text-xs font-normal text-gray-500 border-r border-[#E2E5EA] bg-page-bg"
                      style={{ minWidth: '90px' }}
                    >
                      Min
                    </TableHead>
                    <TableHead 
                      className="px-2 py-2 text-center text-xs font-normal text-gray-500 border-r border-[#E2E5EA] bg-page-bg"
                      style={{ minWidth: '90px' }}
                    >
                      Max
                    </TableHead>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {createDataFromPqrReport.map((row: any, rowIndex: number) => {
                return (
                  <TableRow
                    key={rowIndex}
                    className={`h-[52px] bg-white ${rowIndex < createDataFromPqrReport.length - 1 ? 'border-b border-[#EEF0F3]' : ''} transition-colors`}
                  >
                    <TableCell className="px-3 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap font-medium bg-white text-xs">
                      {row.passName}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-center border-r border-[#E2E5EA] whitespace-nowrap">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                        title="View Records for Pass"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(row.passName);
                        }}
                      >
                        <Eye className="h-5 w-5 text-grey-600" />
                      </button>
                    </TableCell>
                    {columnDefinitions.map((column, colIndex) => (
                      <React.Fragment key={colIndex}>
                        <TableCell 
                          data-editable={column.editable ? "true" : undefined}
                          className={`px-2 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs bg-white ${column.editable ? 'p-0' : ''}`}
                          style={{ minWidth: '90px' }}
                          onMouseDown={column.editable ? (e) => e.stopPropagation() : undefined}
                          onClick={column.editable ? (e) => e.stopPropagation() : undefined}
                        >
                          {column.editable ? (
                            <input
                              type="number"
                              inputMode="numeric"
                              step="any"
                              className="w-16 h-7 rounded-md border border-[#E2E5EA] bg-white px-1 py-0.5 text-xs text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-hero-red/30 focus:border-hero-red mx-auto"
                              value={editedSummaryValues[`${row.passName}:${column.minKey}`] ?? String(row[column.minKey] ?? '0')}
                              onMouseDown={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const key = `${row.passName}:${column.minKey}`;
                                const next = e.target.value;
                                setEditedSummaryValues((prev) => ({ ...prev, [key]: next }));
                              }}
                            />
                          ) : (
                            (row[column.minKey] ?? '0')
                          )}
                        </TableCell>
                        <TableCell 
                          data-editable={column.editable ? "true" : undefined}
                          className={`px-2 py-2 text-center text-gray-700 border-r border-[#E2E5EA] whitespace-nowrap text-xs bg-white ${column.editable ? 'p-0' : ''}`}
                          style={{ minWidth: '90px' }}
                          onMouseDown={column.editable ? (e) => e.stopPropagation() : undefined}
                          onClick={column.editable ? (e) => e.stopPropagation() : undefined}
                        >
                          {column.editable ? (
                            <input
                              type="number"
                              inputMode="numeric"
                              step="any"
                              className="w-16 h-7 rounded-md border border-[#E2E5EA] bg-white px-1 py-0.5 text-xs text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-hero-red/30 focus:border-hero-red mx-auto"
                              value={editedSummaryValues[`${row.passName}:${column.maxKey}`] ?? String(row[column.maxKey] ?? '0')}
                              onMouseDown={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const key = `${row.passName}:${column.maxKey}`;
                                const next = e.target.value;
                                setEditedSummaryValues((prev) => ({ ...prev, [key]: next }));
                              }}
                            />
                          ) : (
                            (row[column.maxKey] ?? '0')
                          )}
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden flex flex-col bg-page-bg">
        <DialogHeader className="flex-shrink-0 px-6 py-4 pr-14 border-b border-[#E2E5EA] bg-white flex flex-col gap-3">
          {/* Row 1: Back button and Create PQR button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnifiedBack}
                disabled={isLoading}
                className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
              >
                <ArrowLeft className="h-4 w-4 text-text-grey" />
              </Button>
              {showExistingReports && (
                <div className="flex flex-col gap-0.5">
                  <h2 className="text-[18px] font-semibold text-blueprint-blue leading-tight">PQR Report</h2>
                  {selectedPipeSize && (
                    <span className="text-[13px] text-heritage-grey">{selectedPipeSize}</span>
                  )}
                </div>
              )}
              {!showExistingReports && effectiveSelectedWeldNumbers.length > 0 && !showLogTable && (
                <div className="flex flex-col gap-1">
                  <h1 className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                    {effectiveSelectedWeldNumbers.join(', ')}
                  </h1>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {currentPqrNumber && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium text-blueprint-blue bg-[rgba(0,39,63,0.06)]">
                        PQR: {currentPqrNumber}
                      </span>
                    )}
                    {showDetailsTable && selectedPassName && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium text-galvanizing-green bg-[rgba(31,154,58,0.08)]">
                        Pass: {selectedPassName}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {showLogTable && selectedRecord && (
                <div className="flex flex-col gap-1">
                  <h1 className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                    {(() => {
                      const raw = selectedRecord.weldNumber ?? selectedRecord.weld_number ?? '';
                      const weldNum = typeof raw === 'string' && raw.includes('|') ? raw.split('|')[0].trim() : raw;
                      return weldNum || (effectiveSelectedWeldNumbers.length > 0 ? effectiveSelectedWeldNumbers.join(', ') : '-');
                    })()}
                  </h1>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {currentPqrNumber && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium text-blueprint-blue bg-[rgba(0,39,63,0.06)]">
                        PQR: {currentPqrNumber}
                      </span>
                    )}
                    {(selectedRecord.passName || selectedRecord.pass_name || selectedPassName) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium text-galvanizing-green bg-[rgba(31,154,58,0.08)]">
                        Pass: {selectedRecord.passName || selectedRecord.pass_name || selectedPassName}
                      </span>
                    )}
                    {selectedRecord.torch && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium text-text-grey bg-page-bg">
                        Torch: {selectedRecord.torch}
                      </span>
                    )}
                    {(selectedRecord.bugType || selectedRecord.bug_type) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium text-readable-red bg-[rgba(196,0,31,0.06)]">
                        Bug Type: {selectedRecord.bugType || selectedRecord.bug_type}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {showExistingReports && !showCreatePqrMode && !isSuccess && !showDetailsTable && !showLogTable && (
              <Button
                onClick={() => {
                  setShowCreatePqrModal(true);
                }}
                className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create PQR
              </Button>
            )}
            {!showExistingReports && (isSuccess || showDetailsTable || showLogTable) && (
              <div className="flex items-center gap-3">
                {(() => {
                  const url = getCurrentPqrReportUrl();
                  const isGenerating = !url && isLoadingExistingReport;
                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-medium text-text-grey">Tolerance:</span>
                        <input
                          type="text"
                          value={tolerance}
                          onChange={(e) => setTolerance(e.target.value)}
                          className="h-9 w-20 px-3 border border-[#E2E5EA] rounded-md outline-none text-[13px] text-text-grey text-center focus:ring-2 focus:ring-hero-red/30 focus:border-hero-red transition-colors"
                          placeholder="10%"
                          title="Tolerance percentage"
                        />
                      </div>
                      <Button
                        onClick={generatePqrReport}
                        disabled={isLoading || effectiveSelectedWeldNumbers.length === 0 || !hasUpdateChanges}
                        variant="outline"
                        className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg text-sm font-medium"
                      >
                        Update
                      </Button>
                      <Button
                        onClick={url ? handleDownloadReport : undefined}
                        disabled={!url}
                        className="h-9 px-4 bg-hero-red hover:bg-hero-red/90 text-white text-sm font-medium"
                        title={url ? 'Download PQR report' : 'Generating PQR report...'}
                      >
                        {url ? (
                          <Download className="h-4 w-4 mr-2" />
                        ) : (
                          <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                        )}
                        {url ? 'Download' : 'Generating'}
                      </Button>
                      <Button
                        onClick={handleShareReport}
                        variant="outline"
                        className="h-9 px-4 border-[#E2E5EA] text-text-grey hover:bg-page-bg text-sm font-medium"
                        title="Share report link"
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </Button>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="py-4 flex-1 overflow-y-auto flex flex-col min-h-0 w-full min-w-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="space-y-4 flex-1 flex flex-col min-h-0 w-full min-w-0">
            {/* Existing reports table (loaded on modal open) */}
            {showExistingReports && (
              <div className="px-6">
                <div className="rounded-lg border border-[#E2E5EA] bg-white overflow-hidden">
                  {isLoadingReportsWithPassView ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hero-red" />
                        <span className="text-text-grey text-sm">Loading reports...</span>
                      </div>
                    </div>
                  ) : reportsWithPassViewError ? (
                    <div className="p-4 text-sm text-readable-red bg-[rgba(196,0,31,0.06)]">
                      {reportsWithPassViewError}
                    </div>
                  ) : reportsWithPassView.length === 0 ? (
                    <div className="p-8 text-center text-sm text-heritage-grey">No PQR reports yet. Click "Create PQR" to generate one.</div>
                  ) : (
                    <div className="max-h-[65vh] overflow-y-auto">
                      <Table className="min-w-full w-full" style={{ tableLayout: 'fixed' }}>
                        <TableHeader className="sticky top-0 z-20">
                          <TableRow>
                            <TableHead className="text-center" style={{ width: '10%' }}>
                              <span className="inline-flex items-center justify-center gap-2">
                                <Hash className="h-3 w-3" />
                                Sl.no
                              </span>
                            </TableHead>
                            <TableHead className="text-left" style={{ width: '30%' }}>
                              <span className="inline-flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                PQR Number
                              </span>
                            </TableHead>
                            <TableHead className="text-left" style={{ width: '40%' }}>
                              <span className="inline-flex items-center gap-2">
                                <Target className="h-4 w-4" />
                                Weld Number
                              </span>
                            </TableHead>
                            <TableHead className="text-center" style={{ width: '20%' }}>
                              <span className="inline-flex items-center justify-center gap-2">
                                <Settings className="h-4 w-4" />
                                Actions
                              </span>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reportsWithPassView.map((row, idx) => {
                            const welds = (row.weld_numbers || '')
                              .split(',')
                              .map((w) => w.trim())
                              .filter(Boolean);

                            return (
                              <TableRow key={row.id ?? idx}>
                                <TableCell className="text-center" style={{ width: '10%' }}>{idx + 1}</TableCell>

                                <TableCell className="text-left" style={{ width: '30%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                  {row.pqr_number ?? '-'}
                                </TableCell>

                                <TableCell className="text-left" style={{ width: '40%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                  {welds.join(', ')}
                                </TableCell>

                                <TableCell className="text-center" style={{ width: '20%' }}>
                                  <div className="flex items-center justify-center gap-3 flex-wrap">
                                    <button
                                      type="button"
                                      className="inline-flex items-center justify-center text-heritage-grey hover:text-blueprint-blue transition-colors"
                                      title="View PQR report"
                                      onClick={() =>
                                        handleViewPqrFromRow(
                                          welds,
                                          row.boundary,
                                          row.pqr_report_url,
                                          row.pqr_number,
                                          row.excluded_tlogs_key?.excluded_tlogs,
                                          row.pqr_limits_mode
                                        )
                                      }
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                    {(row.pass_view_reports || []).length > 0 ? (
                                      (row.pass_view_reports || []).length === 1 ? (
                                        <a
                                          href={(row.pass_view_reports || [])[0].report_url}
                                          download
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex items-center justify-center text-heritage-grey hover:text-blueprint-blue transition-colors"
                                          title="Download Pass View Report"
                                        >
                                          <Download className="h-4 w-4" />
                                        </a>
                                      ) : (
                                        <button
                                          type="button"
                                          className="inline-flex items-center justify-center text-heritage-grey hover:text-blueprint-blue transition-colors"
                                          title="Pass View Reports"
                                          onClick={() => {
                                            setSelectedPassViewReports(row.pass_view_reports || []);
                                            setShowPassViewReportsModal(true);
                                          }}
                                        >
                                          <Download className="h-4 w-4" />
                                        </button>
                                      )
                                    ) : null}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center h-full min-h-[200px]">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
                  <span className="text-gray-600 text-lg">Generating PQR report...</span>
                </div>
              </div>
            )}

            {/* Success State */}
            {isSuccess && (
              <div className="flex-1 min-h-0 overflow-hidden w-full min-w-0">
                {showLogTable ? renderLogTable() : showDetailsTable ? renderDetailsTable() : renderSummaryTable()}
              </div>
            )}

            {/* Loading Log State */}
            {isLoadingLog && (
              <div className="flex items-center justify-center h-full min-h-[200px]">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
                  <span className="text-gray-600 text-lg">Loading log data...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Create PQR Modal */}
      <Dialog open={showCreatePqrModal} onOpenChange={setShowCreatePqrModal}>
        <DialogContent className="max-w-6xl max-h-[85vh] [&>button]:hidden">
          <DialogHeader>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowCreatePqrModal(false);
                    // If we're in Create PQR mode, go back to Existing Reports
                    if (showCreatePqrMode) {
                      setShowCreatePqrMode(false);
                      setShowExistingReports(true);
                      setIsSuccess(false);
                      setError(null);
                      setResponseData(null);
                      setShowDetailsTable(false);
                      setShowLogTable(false);
                      setLogData(null);
                      setSelectedRecord(null);
                      setSelectedLogRecords(new Set());
                      setExcludedTlogsData([]);
                      setEditedSummaryValues({});
                    }
                  }}
                  className="h-8 w-8"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <DialogTitle>Create PQR</DialogTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowCreatePqrModal(false);
                  if (showCreatePqrMode) {
                    setShowCreatePqrMode(false);
                    setShowExistingReports(true);
                    setIsSuccess(false);
                    setError(null);
                    setResponseData(null);
                    setShowDetailsTable(false);
                    setShowLogTable(false);
                    setLogData(null);
                    setSelectedRecord(null);
                    setSelectedLogRecords(new Set());
                    setExcludedTlogsData([]);
                    setEditedSummaryValues({});
                  }
                }}
                className="h-8 w-8 shrink-0"
                title="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* All inputs on one line, labels above */}
            <div className="flex flex-wrap items-end gap-6">
              <div className="flex flex-col gap-1.5 w-96 min-w-[280px]">
                <label className="text-sm font-medium text-gray-700">Select Weld(s)</label>
                {selectedWeldNumbers.length > 0 ? (
                  <MultiSelect
                    options={selectedWeldNumbers.map((weld) => ({ value: weld, label: weld }))}
                    selected={selectedWeldNumbersForApi}
                    onChange={(values) => {
                      setSelectedWeldNumbersForApi(values);
                      setIsSuccess(false);
                      setError(null);
                      setResponseData(null);
                      setShowDetailsTable(false);
                      setShowLogTable(false);
                      setLogData(null);
                      setSelectedRecord(null);
                      setSelectedLogRecords(new Set());
                      setExcludedTlogsData([]);
                      setExistingReportInfo(null);
                    }}
                    placeholder="Select weld(s)..."
                  />
                ) : (
                  <span className="text-sm text-gray-500 py-1.5">No welds available</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5 w-96 min-w-[280px]">
                <label className="text-sm font-medium text-gray-700">PQR Number</label>
                <input
                  type="text"
                  value={createPqrReportName}
                  onChange={(e) => setCreatePqrReportName(e.target.value.replace(/[^a-zA-Z0-9\s-_]/g, ''))}
                  className="h-10 px-3 border border-gray-300 rounded-md outline-none text-sm w-full focus:ring-2 focus:ring-blue-500 focus:"
                  placeholder=""
                />
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <label className="text-sm font-medium text-gray-700">Tolerance</label>
                <input
                  type="text"
                  value={tolerance}
                  onChange={(e) => setTolerance(e.target.value)}
                  className="h-10 px-3 border border-gray-300 rounded-md outline-none text-sm w-20 text-center focus:ring-2 focus:ring-blue-500 focus:"
                  placeholder="10%"
                />
              </div>
            </div>

            {/* Limits selection (MinOfMin/MaxOfMax vs MinOfAvg/MaxOfAvg) */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">Limits Selection</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    pqrLimitsMode === 'avg' ? ' bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pqrLimitsMode"
                    value="avg"
                    checked={pqrLimitsMode === 'avg'}
                    onChange={() => setPqrLimitsMode('avg')}
                    className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">MinOfAvg / MaxOfAvg</span>
                    <p className="text-xs text-gray-500 mt-1">Use minimum and maximum based on averages</p>
                  </div>
                </label>

                <label
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    pqrLimitsMode === 'minmax' ? ' bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pqrLimitsMode"
                    value="minmax"
                    checked={pqrLimitsMode === 'minmax'}
                    onChange={() => setPqrLimitsMode('minmax')}
                    className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">MinOfMin / MaxOfMax</span>
                    <p className="text-xs text-gray-500 mt-1">Use minimum of mins and maximum of maxes</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              {existingReportInfo?.data?.pqrReport?.pqr_report_url && (
                <div
                  className="flex items-center relative cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={handleDownloadReport}
                  title="Download PQR Report"
                >
                  <FileText className="h-5 w-5 text-red-600" />
                </div>
              )}
              <Button
                onClick={() => {
                  setShowCreatePqrModal(false);
                  setShowCreatePqrMode(true);
                  setShowExistingReports(false);
                  generatePqrReport();
                }}
                disabled={isLoading || effectiveSelectedWeldNumbers.length === 0}
                className="px-4 py-1.5 text-sm font-medium"
              >
                Generate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pass View Reports Modal */}
      <Dialog open={showPassViewReportsModal} onOpenChange={setShowPassViewReportsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col [&>button]:hidden">
          <DialogHeader>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowPassViewReportsModal(false)}
                  className="h-8 w-8"
                >
                  <ArrowLeft className="h-4 w-4 text-gray-500" />
                </Button>
                <DialogTitle>Pass View Reports</DialogTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowPassViewReportsModal(false)}
                className="h-8 w-8 shrink-0"
                title="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {selectedPassViewReports.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No pass view reports available</div>
            ) : (
              <div className="space-y-3">
                {selectedPassViewReports.map((report, idx) => (
                  <div
                    key={report.id ?? idx}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-600" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Weld Number: {report.weld_number || '-'}
                        </div>
                        {/* {report.generated_at && (
                          <div className="text-xs text-gray-500 mt-1">
                            Generated: {new Date(report.generated_at).toLocaleString()}
                          </div>
                        )} */}
                      </div>
                    </div>
                    <a
                      href={report.report_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-grey-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
